import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { API, useAuth } from "../App";
import { Section, GlassCard, Eyebrow } from "../components/Section";
import {
  MessageSquare,
  MapPin,
  Clock,
  ChevronRight,
  Loader2,
  Sparkles,
  Inbox as InboxIcon,
  ArrowRight,
} from "lucide-react";

// status -> display tone + label
const STATUS_MAP = {
  open: { label: "Awaiting Lawyer", tone: "bg-gold-400/10 text-gold-200 border-gold-400/30" },
  accepted: { label: "Lawyer Assigned", tone: "bg-turquoise/10 text-turquoise border-turquoise/30" },
  consultation_scheduled: { label: "Consultation Scheduled", tone: "bg-turquoise/10 text-turquoise border-turquoise/30" },
  engagement_active: { label: "Engagement Active", tone: "bg-turquoise/10 text-turquoise border-turquoise/30" },
  filing_stage: { label: "Filing Stage", tone: "bg-turquoise/10 text-turquoise border-turquoise/30" },
  hearing_stage: { label: "Hearing Stage", tone: "bg-turquoise/10 text-turquoise border-turquoise/30" },
  judgment_pending: { label: "Judgment Pending", tone: "bg-gold-400/10 text-gold-200 border-gold-400/30" },
  matter_closed: { label: "Matter Closed", tone: "bg-white/5 text-white/55 border-white/15" },
};

// Pipeline visualization (matches the lawyer-side states)
const PIPELINE = [
  { key: "open", label: "Submitted" },
  { key: "accepted", label: "Accepted" },
  { key: "consultation_scheduled", label: "Consultation" },
  { key: "engagement_active", label: "Engagement" },
  { key: "filing_stage", label: "Filing" },
  { key: "hearing_stage", label: "Hearing" },
  { key: "judgment_pending", label: "Judgment" },
  { key: "matter_closed", label: "Closed" },
];

const urgencyTone = {
  high: "bg-red-400/10 text-red-300 border-red-400/30",
  medium: "bg-gold-400/10 text-gold-200 border-gold-400/30",
  low: "bg-turquoise/10 text-turquoise border-turquoise/30",
};

const MyRequests = () => {
  const { user, loading } = useAuth();
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(true);
  const [filter, setFilter] = useState("all"); // all, open, in_progress, closed

  useEffect(() => {
    if (!user) return;
    (async () => {
      setBusy(true);
      try {
        const { data } = await axios.get(`${API}/lawyer-requests/my-requests`, { withCredentials: true });
        setRequests(data.requests || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load your requests");
      } finally {
        setBusy(false);
      }
    })();
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" state={{ from: { pathname: "/my-requests" } }} replace />;

  const filtered = requests.filter((r) => {
    if (filter === "all") return true;
    if (filter === "open") return r.status === "open";
    if (filter === "closed") return r.status === "matter_closed";
    if (filter === "in_progress") return r.status !== "open" && r.status !== "matter_closed";
    return true;
  });

  const counts = {
    all: requests.length,
    open: requests.filter((r) => r.status === "open").length,
    in_progress: requests.filter((r) => r.status !== "open" && r.status !== "matter_closed").length,
    closed: requests.filter((r) => r.status === "matter_closed").length,
  };

  const pipelineIndex = (status) => {
    const i = PIPELINE.findIndex((p) => p.key === status);
    return i === -1 ? 0 : i;
  };

  return (
    <main className="bg-ink text-white min-h-[calc(100vh-5rem)] py-10" data-testid="my-requests-page">
      <div className="container mx-auto px-5 sm:px-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Eyebrow>Your Matters</Eyebrow>
            <h1 className="font-playfair text-3xl md:text-5xl text-white mt-3 leading-tight">
              My <span className="italic text-aj-gradient">Requests</span> &amp; Cases.
            </h1>
            <p className="font-inter text-sm text-white/55 mt-2">
              Track every legal matter you&apos;ve submitted — from inbox to judgment.
            </p>
          </div>
          <Link to="/ai-assistant" data-testid="my-req-new" className="btn-aj-primary">
            <Sparkles className="w-4 h-4" /> Start a New Matter
          </Link>
        </div>

        {/* Filter chips */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: "all", l: "All", c: counts.all },
            { id: "open", l: "Awaiting Lawyer", c: counts.open },
            { id: "in_progress", l: "In Progress", c: counts.in_progress },
            { id: "closed", l: "Closed", c: counts.closed },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`my-req-filter-${f.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-montserrat text-[11px] uppercase tracking-wider transition-all ${
                filter === f.id
                  ? "bg-turquoise text-ink font-semibold"
                  : "bg-transparent text-white/70 border border-white/15 hover:border-white/40 hover:text-white"
              }`}
            >
              {f.l}
              <span className="px-1.5 rounded-full bg-black/30 text-[9px]">{f.c}</span>
            </button>
          ))}
        </div>

        {busy && (
          <div className="flex items-center gap-2 text-white/55 font-inter text-sm mb-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your matters…
          </div>
        )}

        {!busy && filtered.length === 0 && (
          <GlassCard className="text-center !py-12" data-testid="my-req-empty">
            <InboxIcon className="w-10 h-10 text-white/30 mx-auto mb-4" />
            <h3 className="font-playfair text-2xl text-white mb-2">No matters yet.</h3>
            <p className="font-inter text-sm text-white/55 mb-6 max-w-md mx-auto">
              When you submit a request through Justice-AI, it will appear here with live status updates.
            </p>
            <Link to="/ai-assistant" className="btn-aj-primary">
              Talk to Justice-AI <ArrowRight className="w-4 h-4" />
            </Link>
          </GlassCard>
        )}

        <div className="space-y-4">
          {filtered.map((r, i) => {
            const idx = pipelineIndex(r.status);
            const status = STATUS_MAP[r.status] || STATUS_MAP.open;
            return (
              <motion.div
                key={r.request_id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
              >
                <GlassCard glow data-testid={`my-req-card-${i}`}>
                  {/* Top row */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="aj-chip !py-1 !text-[9px]">{r.category}</span>
                    <span className={`px-2.5 py-0.5 rounded-full border font-montserrat text-[10px] uppercase tracking-wider ${urgencyTone[r.urgency] || urgencyTone.medium}`}>
                      {r.urgency} urgency
                    </span>
                    {r.is_wallet_eligible && (
                      <span className="aj-chip aj-chip-gold !py-1 !text-[9px]">Justice Wallet</span>
                    )}
                    <span className={`ml-auto px-2.5 py-0.5 rounded-full border font-montserrat text-[10px] uppercase tracking-wider ${status.tone}`}>
                      {status.label}
                    </span>
                  </div>

                  {/* Body */}
                  <h3 className="font-playfair text-xl text-white mb-1">{r.category} matter</h3>
                  <div className="flex items-center gap-3 font-inter text-xs text-white/55 mb-3 flex-wrap">
                    <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {r.location}</span>
                    <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {new Date(r.created_at).toLocaleString()}</span>
                    {r.assigned_lawyer_name && (
                      <span className="inline-flex items-center gap-1.5 text-turquoise">
                        <MessageSquare className="w-3.5 h-3.5" /> Lawyer: {r.assigned_lawyer_name}
                      </span>
                    )}
                  </div>
                  <p className="font-inter text-sm text-white/75 leading-relaxed mb-5">{r.summary}</p>

                  {/* Pipeline */}
                  <div className="rounded-xl border border-white/8 bg-white/[0.02] p-4">
                    <Eyebrow className="mb-3">Progress</Eyebrow>
                    <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
                      {PIPELINE.map((p, j) => {
                        const reached = j <= idx;
                        const current = j === idx;
                        return (
                          <div key={p.key} className="flex items-center gap-1.5 shrink-0">
                            <div className={`flex flex-col items-center gap-1.5 min-w-[64px]`}>
                              <div className={`w-3 h-3 rounded-full border-2 transition-all ${
                                current ? "bg-turquoise border-turquoise shadow-[0_0_12px_rgba(0,200,215,0.6)]"
                                : reached ? "bg-turquoise/70 border-turquoise/70"
                                : "bg-transparent border-white/20"
                              }`} />
                              <div className={`font-montserrat text-[9px] uppercase tracking-wider text-center leading-tight ${
                                current ? "text-turquoise font-semibold" : reached ? "text-white/70" : "text-white/30"
                              }`}>
                                {p.label}
                              </div>
                            </div>
                            {j < PIPELINE.length - 1 && (
                              <ChevronRight className={`w-3 h-3 ${reached ? "text-turquoise/40" : "text-white/15"}`} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            );
          })}
        </div>
      </div>
    </main>
  );
};

export default MyRequests;
